import Map from 'ol/Map';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { View } from 'ol';
import { useEffect, useState } from 'react';
import { MapPresenter } from '../../../map/MapPresenter';
import { MapComponent } from '../../../map/MapV3';
import { epsg3857 } from '../../../map/maputils';
import { InlineMatchedFeature } from '../Inline.types';

/**
 * Draws the `matched_feature` geometries of INLINE notifications on a map and fits the
 * view to them. Shared by the INLINE map page (all currently listed notifications) and
 * the notification detail page (a single notification).
 *
 * The features arrive from the API as GeoJSON strings; entries that cannot be parsed or
 * that carry no geometry are skipped. The base map layer is added by MapPresenter.
 */
export default function InlineFeatureMap(props: { matched_features: string[], height?: string, width?: string }) {
    const [source] = useState<VectorSource>(() => new VectorSource({ wrapX: true }));
    const [layer] = useState<VectorLayer>(() => new VectorLayer({
        source: source,
        style: new Style({
            fill: new Fill({ color: "rgba(204, 0, 0, 0.2)" }),
            stroke: new Stroke({ color: "#cc0000", width: 1 }),
        }),
    }));
    const [map] = useState<Map>(() => new Map({
        layers: [
            layer,
        ],
        view: new View({
            projection: "EPSG:3857",
            center: epsg3857({ lat: 50, lon: 15 }),
            zoom: 4,
            minZoom: 3,
            maxZoom: 18,
        }),
        controls: [],
    }));
    const [presenter] = useState<MapPresenter>(() => new MapPresenter(map));

    useEffect(() => {
        const parsed = props.matched_features
            .map((matched_feature) => {
                if (!matched_feature) {
                    return null;
                }
                try {
                    return JSON.parse(matched_feature) as InlineMatchedFeature;
                } catch (error) {
                    console.warn("Failed to parse matched_feature", error);
                    return null;
                }
            })
            .filter((feature): feature is InlineMatchedFeature => !!feature && !!feature.geometry);

        const features = new GeoJSON().readFeatures({ type: "FeatureCollection", features: parsed }, {
            dataProjection: "EPSG:4326",
            featureProjection: "EPSG:3857",
        });

        source.clear();
        source.addFeatures(features);

        if (features.length > 0) {
            const extent = source.getExtent();
            if (extent && extent.every(Number.isFinite)) {
                map.getView().fit(extent, { padding: [30, 30, 30, 30], maxZoom: 10 });
            }
        }
    }, [props.matched_features, map, source]);

    return <MapComponent presenter={presenter} height={props.height || "calc(100vh - 8rem)"} width={props.width || "100%"} />;
}
